import { useMemo } from "react"
import { SwapTransactionHistory } from "@mezo/api"
import {
  Block,
  Button,
  ListItem,
  ListItemLabel,
  ParagraphSmall,
  useStyletron,
} from "@mezo-org/mezo-clay"

import { selectWalletBalances, useDappSelector } from "#/redux-state"
import {
  bigIntToHumanReadableFormat,
  formatTimestampToLocaleTime,
  getCurrencySymbol,
  getMezoExplorerUrl,
  getTokenIcon,
} from "#/shared/utils"

type OverviewSwapTransactionItemProps = Pick<
  SwapTransactionHistory,
  "timestamp" | "transactionHash" | "fromToken" | "toToken" | "toAmount"
>

export default function OverviewSwapTransactionItem({
  timestamp,
  transactionHash,
  fromToken,
  toToken,
  toAmount,
}: OverviewSwapTransactionItemProps) {
  const [, theme] = useStyletron()

  const walletAssets = useDappSelector(selectWalletBalances)

  const explorerLink = useMemo(
    () => getMezoExplorerUrl(transactionHash),
    [transactionHash],
  )

  const fromSymbol = getCurrencySymbol(fromToken)
  const toSymbol = getCurrencySymbol(toToken)

  const toAmountLabel = bigIntToHumanReadableFormat(
    BigInt(toAmount),
    Number(walletAssets[toSymbol].decimals),
  )

  return (
    <ListItem
      overrides={{
        ArtworkContainer: {
          style: () => ({ width: "80px", height: "80px" }),
        },
        Content: { style: () => ({ borderBottom: "none" }) },
        Root: {
          style: () => ({
            borderBottom: `1px solid ${theme.colors.borderOpaque}`,
          }),
        },
      }}
      artwork={() => (
        <Block
          as="img"
          src={getTokenIcon(toToken)}
          height="48px"
          width="48px"
        />
      )}
      endEnhancer={() => (
        <Button
          $as="a"
          shape="pill"
          size="small"
          kind="secondary"
          href={explorerLink}
          target="_blank"
        >
          See on Explorer
        </Button>
      )}
    >
      <ListItemLabel
        description={
          <>
            <ParagraphSmall
              as="span"
              display="block"
              color={theme.colors.contentSecondary}
            >
              <Block as="span" color={theme.colors.positive}>
                +{toAmountLabel}
              </Block>{" "}
              {toSymbol}
            </ParagraphSmall>
            <ParagraphSmall
              as="span"
              display="block"
              color={theme.colors.contentSecondary}
            >
              {formatTimestampToLocaleTime(timestamp / 1000)}
            </ParagraphSmall>
          </>
        }
      >
        Swapped {fromSymbol} for {toSymbol}
      </ListItemLabel>
    </ListItem>
  )
}
